import { Incident, CatalogTask, Deployment } from '@/types/dashboard';

export interface AIAnalysis {
  predictions: {
    nextWeekIncidents: number;
    highRiskAreas: string[];
    recommendedActions: string[];
  };
  patterns: {
    commonRootCauses: string[];
    recurringIssues: string[];
    impactAreas: string[];
  };
  recommendations: {
    deployment: {
      optimalTiming: Date;
      riskLevel: string;
      requiredResources: string[];
    };
    system: {
      capacityPlanning: string[];
      optimizationSuggestions: string[];
      preventiveActions: string[];
    };
  };
}

export class AIService {
  private static instance: AIService;
  private apiEndpoint: string;
  private apiKey: string;

  private constructor() {
    this.apiEndpoint = process.env.REACT_APP_AI_API_ENDPOINT || '';
    this.apiKey = process.env.REACT_APP_AI_API_KEY || '';
  }

  public static getInstance(): AIService {
    if (!AIService.instance) {
      AIService.instance = new AIService();
    }
    return AIService.instance;
  }

  async analyzeIncidents(incidents: Incident[]): Promise<AIAnalysis> {
    try {
      const response = await fetch(`${this.apiEndpoint}/analyze/incidents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({ incidents })
      });

      if (!response.ok) {
        throw new Error('Failed to analyze incidents');
      }

      return await response.json();
    } catch (error) {
      console.error('Error analyzing incidents:', error);
      // Return fallback analysis if AI service fails
      return this.getFallbackAnalysis(incidents);
    }
  }

  private getFallbackAnalysis(incidents: Incident[]): AIAnalysis {
    // Basic statistical analysis as fallback
    const totalIncidents = incidents.length;
    const recentIncidents = incidents.filter(inc => {
      const date = new Date(inc.Opened);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return date >= thirtyDaysAgo;
    });

    return {
      predictions: {
        nextWeekIncidents: Math.round(totalIncidents / 4), // Simple average
        highRiskAreas: ['System', 'API', 'Database'],
        recommendedActions: ['Monitor system load', 'Review recent deployments']
      },
      patterns: {
        commonRootCauses: ['Deployment issues', 'System overload', 'API failures'],
        recurringIssues: ['Connection timeouts', 'Authentication failures'],
        impactAreas: ['User authentication', 'Data processing']
      },
      recommendations: {
        deployment: {
          optimalTiming: new Date(),
          riskLevel: 'Medium',
          requiredResources: ['Backup system', 'Monitoring tools']
        },
        system: {
          capacityPlanning: ['Increase server capacity', 'Optimize database queries'],
          optimizationSuggestions: ['Implement caching', 'Add load balancing'],
          preventiveActions: ['Regular system checks', 'Update security patches']
        }
      }
    };
  }
} 